// Slim UI navigation — chat only.
import type { RouteId } from "./app-route-paths.ts";
import type { IconName } from "./components/icons.ts";
import { t } from "./i18n/index.ts";

export type NavigationRouteId = RouteId;

// No sidebar nav routes in slim mode — chat is the only page.
export const SIDEBAR_NAV_ROUTES = [] as const satisfies readonly NavigationRouteId[];
export type SidebarNavRoute = never;

export const DEFAULT_SIDEBAR_PINNED_ROUTES = [] as const satisfies readonly SidebarNavRoute[];

export function normalizeSidebarPinnedRoutes(_value: unknown): SidebarNavRoute[] | null {
  return null;
}

export function sidebarMoreRoutes(_pinned: readonly SidebarNavRoute[]): SidebarNavRoute[] {
  return [];
}

type SettingsNavigationGroup = {
  labelKey: string | null;
  routes: readonly NavigationRouteId[];
};

export const SETTINGS_NAVIGATION_GROUPS = [
  { labelKey: null, routes: ["chat"] as const },
] as const satisfies readonly SettingsNavigationGroup[];

export const SETTINGS_NAVIGATION_ROUTES: readonly NavigationRouteId[] =
  SETTINGS_NAVIGATION_GROUPS.flatMap((group) => group.routes);

const NAVIGATION_ICONS: Partial<Record<NavigationRouteId, IconName>> = {
  chat: "messageSquare",
};

export function isSettingsNavigationRoute(routeId: NavigationRouteId): boolean {
  return (SETTINGS_NAVIGATION_ROUTES as readonly NavigationRouteId[]).includes(routeId);
}

export function navigationIconForRoute(routeId: NavigationRouteId): IconName {
  return NAVIGATION_ICONS[routeId] ?? "folder";
}

export function scheduleRoutePreload<TRouteId extends string>(
  _timers: Map<EventTarget, ReturnType<typeof globalThis.setTimeout>>,
  _routeId: TRouteId,
  _event: Event,
  _preload: ((routeId: TRouteId) => Promise<void> | void) | undefined,
  _disabled = false,
  _immediate = false,
) {
  // No-op in slim mode
}

export function cancelRoutePreload(
  _timers: Map<EventTarget, ReturnType<typeof globalThis.setTimeout>>,
  _event: Event,
) {
  // No-op in slim mode
}

const NAVIGATION_COPY: Partial<Record<NavigationRouteId, { titleKey: string; subtitleKey: string }>> = {
  chat: { titleKey: "tabs.chat", subtitleKey: "subtitles.chat" },
};

export function titleForRoute(routeId: NavigationRouteId): string {
  const copy = NAVIGATION_COPY[routeId];
  return copy ? t(copy.titleKey) : "";
}

export function settingsNavigationLabelForRoute(routeId: NavigationRouteId): string {
  return titleForRoute(routeId);
}

export function subtitleForRoute(routeId: NavigationRouteId): string {
  const copy = NAVIGATION_COPY[routeId];
  return copy ? t(copy.subtitleKey) : "";
}
